'use client';

import { useEffect, useRef } from 'react';

import { useDesktopAuthStore } from '@/models/desktopAuthSlice';

/**
 * Watchdog window for a desktop sign-in attempt (MUL-3346). Silent
 * abandonment of the browser leg produces no FE signal, so this is the only
 * recovery path for that case: long enough to absorb the accidental
 * second-click gap the bug is about, short enough not to strand a user who
 * closed the browser without finishing.
 *
 * Tuned down from the original 30s: the accidental double click lands within
 * a second or two of the first, so 5s still covers it, and a user who closed
 * the browser gets their buttons back almost immediately instead of watching
 * a dead control for half a minute. The trade is that 5s is shorter than a
 * real credential entry, so a *deliberate* re-click mid-attempt is now easy
 * to reach — that path is DSK-424 (Rust single-slot `pending_attempt`), not
 * something this window guards.
 */
export const DESKTOP_LOGIN_PENDING_TIMEOUT_MS = 5_000;

/**
 * True while a desktop sign-in attempt is in flight (`loginPendingSince` set
 * by `launchDesktopAuthLogin`). Arms a timeout for the remainder of the
 * watchdog window and clears the store entry when it elapses — derived from
 * the timestamp, so a surface mounted late still expires a stale entry on
 * schedule. Always false on web builds, where the funnel never runs.
 *
 * `onSettled` fires on the falling edge — the attempt this component watched
 * resolve (login success, auth error, launch failure, or watchdog expiry) —
 * so sign-in buttons can release their local spinner. It intentionally does
 * NOT fire on mount when nothing was pending.
 *
 * Expiry only re-enables the sign-in controls; it does not cancel the
 * underlying attempt, so a login completed in the original browser tab after
 * expiry still lands via `auth:login-success`.
 */
export function useDesktopLoginPending(onSettled?: () => void): boolean {
  const loginPendingSince = useDesktopAuthStore((state) => state.loginPendingSince);
  const loginSettleEpoch = useDesktopAuthStore((state) => state.loginSettleEpoch);

  const onSettledRef = useRef(onSettled);
  useEffect(() => {
    onSettledRef.current = onSettled;
  });

  // Seeded from the epoch at mount so an attempt resolved before this consumer
  // existed is not replayed as a settle.
  const lastSettleEpochRef = useRef(loginSettleEpoch);
  const wasPendingRef = useRef(false);
  useEffect(() => {
    if (loginPendingSince === null) {
      // Two ways to learn the attempt is over. `wasPendingRef` is the normal
      // one — we rendered while it was pending. The epoch is the backstop for
      // a settle that landed in the same batch as the launch, where no render
      // ever observed the flag set: the caller still set its spinner
      // synchronously in the click handler, so without this it would never be
      // released and every sign-in button would sit disabled forever.
      const settledUnobserved = loginSettleEpoch !== lastSettleEpochRef.current;
      if (wasPendingRef.current || settledUnobserved) {
        wasPendingRef.current = false;
        lastSettleEpochRef.current = loginSettleEpoch;
        onSettledRef.current?.();
      }
      return;
    }

    wasPendingRef.current = true;
    const remainingMs = Math.max(
      0,
      loginPendingSince + DESKTOP_LOGIN_PENDING_TIMEOUT_MS - Date.now()
    );
    const timer = setTimeout(() => {
      useDesktopAuthStore.getState().clearLoginPending();
    }, remainingMs);

    // The browser leg backgrounds the webview, where setTimeout can be
    // throttled or suspended. The expiry is timestamp-derived, so re-check it
    // whenever the window comes back to the foreground — the moment the user
    // returns to retry is exactly when the release must not be late.
    const expireIfDue = () => {
      if (Date.now() - loginPendingSince >= DESKTOP_LOGIN_PENDING_TIMEOUT_MS) {
        useDesktopAuthStore.getState().clearLoginPending();
      }
    };
    window.addEventListener('focus', expireIfDue);
    document.addEventListener('visibilitychange', expireIfDue);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('focus', expireIfDue);
      document.removeEventListener('visibilitychange', expireIfDue);
    };
  }, [loginPendingSince, loginSettleEpoch]);

  return loginPendingSince !== null;
}
